import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

const NervousSystemShift = () => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.12 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const insights = [
    {
      number: '01',
      title: 'Structure Destabilizes',
      desc: 'When structure destabilizes under prolonged load, presence softens.',
    },
    {
      number: '02',
      title: 'Clarity Diminishes',
      desc: 'Sustained tension patterns reshape facial architecture, diminishing natural clarity.',
    },
    {
      number: '03',
      title: 'Performance Declines',
      desc: 'Performance steadiness declines as nervous system overload persists.',
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-8 sm:py-16 lg:py-22 bg-warmWhite overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-1/2 h-full bg-cream/60 rounded-l-[80px]"></div>
      <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-sand/15 blur-3xl"></div>
      <div className="absolute bottom-20 right-20 w-48 h-48 rounded-full bg-bronze/5 blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-14">
        {/* Our Approach */}
        <div
          className={`max-w-4xl mx-auto text-center mb-8 sm:mb-16 lg:mb-20 transition-all duration-800 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-3 sm:mb-6">
            <div className="w-6 sm:w-8 h-[1px] bg-bronze"></div>
            <span className="text-bronze text-[9px] sm:text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">
              Our Approach
            </span>
            <div className="w-6 sm:w-8 h-[1px] bg-bronze"></div>
          </div>

          <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-light text-charcoal leading-[1.3] mb-3 sm:mb-6">
            At Vionne Clinical Atelier, we decode the silent language of face.
          </h3>

          <p className="text-textGray text-[12px] sm:text-[13px] font-sans font-light leading-[1.8] sm:leading-[1.9] mb-3 sm:mb-5">
            Jaw tightness, brow compression, persistent dullness, and subtle asymmetry are often reflections of cumulative nervous system strain and hormonal fluctuation under load. Your face is not failing; it is compensating.&nbsp;Through precision facial tension mapping and clinical botanical acupuncture, we regulate internal instability to restore visible tone, clarity, and performance steadiness.
          </p>

          <div className="inline-block">
            <p className="font-display text-sm sm:text-base text-charcoal font-medium italic leading-[1.8]">
              Health and beauty are not separate systems.
              <br />
              <span style={{ color: 'rgb(107, 101, 96)', fontFamily: 'Raleway, sans-serif', fontSize: '13px', fontStyle: 'normal' }}>
                True and lasting change begins at the root.
              </span>
            </p>
            <div className="w-20 sm:w-24 h-[1px] bg-gradient-to-r from-transparent via-bronze/60 to-transparent mt-3 sm:mt-4 mx-auto"></div>
          </div>
        </div>

        {/* Top row: headline + image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-10 lg:gap-16 items-center mb-8 sm:mb-14 lg:mb-20">
          {/* Left */}
          <div className={`transition-all duration-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center gap-2 mb-3 sm:mb-6">
              <div className="w-6 sm:w-8 h-[1px] bg-bronze"></div>
              <span className="text-bronze text-[9px] font-sans font-semibold tracking-[0.35em] uppercase">
                The Hidden Pattern
              </span>
            </div>

            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-charcoal leading-[1.3] mb-3 sm:mb-6">
              <span className="italic">Your face</span> is not aging.
              <br />
              It&rsquo;s adapting to
              <br />
              <span className="text-bronze italic">chronic stress.</span>
            </h2>

            <div className="w-12 sm:w-16 h-[1px] bg-gradient-to-r from-bronze/60 to-transparent mb-3 sm:mb-6"></div>

            <p className="text-textGray text-[12px] sm:text-[13px] font-sans font-light leading-[1.8] sm:leading-[1.9] max-w-md mb-3 sm:mb-5">
              Chronic stress reshapes facial structure gradually—tightening, compressing, and holding. It changes how we hold our muscles.&nbsp;It affects circulation. It influences hormones.&nbsp;
            </p>

            <p className="text-textGray text-[12px] sm:text-[13px] font-sans font-light leading-[1.8] sm:leading-[1.9] max-w-md mb-3 sm:mb-5">
              Over time:
              <br />Structure destabilizes.
              <br />Clarity softens.
              <br />Performance steadiness declines.
            </p>

            <p className="text-textGray text-[12px] sm:text-[13px] font-sans font-light leading-[1.8] sm:leading-[1.9] max-w-md mb-3 sm:mb-5">
              <span style={{ color: 'rgb(46, 46, 44)' }}>The Vionne Stability Method™ restores structural stability at its origin — supporting vitality, hormonal steadiness, and sustainable performance under pressure.</span>
            </p>

            <p className="text-charcoal text-[12px] sm:text-[13px] font-sans font-medium leading-[1.8] sm:leading-[1.9] max-w-md mb-4 sm:mb-7">
              Regulation, not enhancement.
              <br />Stability, not strain.
            </p>

            <button
              onClick={() => navigate('/method')}
              className="group inline-flex items-center gap-3 cursor-pointer"
            >
              <span className="px-6 sm:px-7 py-2.5 sm:py-3 bg-charcoal text-white rounded-full text-[9px] sm:text-[10px] font-sans font-semibold tracking-[0.18em] uppercase group-hover:bg-bronze transition-colors duration-400 whitespace-nowrap">
                Explore the Method
              </span>
              <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-charcoal flex items-center justify-center group-hover:bg-bronze transition-colors duration-400">
                <i className="ri-arrow-right-line text-white text-sm group-hover:translate-x-0.5 transition-transform duration-300"></i>
              </span>
            </button>
          </div>

          {/* Right: Image */}
          <div
            className={`relative transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
            style={{ transitionDelay: isVisible ? '300ms' : '0ms' }}
          >
            <div className="relative">
              <div className="relative w-full h-[220px] sm:h-[380px] lg:h-[480px] xl:h-[560px] rounded-2xl sm:rounded-3xl overflow-hidden shadow-elevated">
                <img
                  src="https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/b57ce6dabcca419c5f48c1f25c282a4d.png"
                  alt="High-functioning professional woman receiving facial acupuncture treatment"
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/15 via-transparent to-transparent"></div>
              </div>

              <div
                className={`absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 lg:-left-10 bg-gradient-to-br from-white via-cream/50 to-white rounded-xl sm:rounded-2xl p-3.5 sm:p-5 shadow-elevated border border-bronze/10 max-w-[180px] sm:max-w-[220px] transition-all duration-700 backdrop-blur-sm ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                }`}
                style={{ transitionDelay: isVisible ? '800ms' : '0ms' }}
              >
                <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-bronze/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-pulse-line text-bronze text-sm sm:text-base"></i>
                  </div>
                  <div>
                    <p className="font-display text-[13px] sm:text-[15px] text-charcoal font-medium leading-tight mb-1">
                      Regulation, not enhancement.
                    </p>
                    <div className="w-6 sm:w-8 h-[1px] bg-bronze/30"></div>
                  </div>
                </div>
                <p className="text-textGray text-[10px] sm:text-[11px] font-sans font-light leading-relaxed">
                  Structural change from within
                </p>
              </div>

              <div className="absolute -top-4 -right-4 w-16 h-16 sm:w-24 sm:h-24 rounded-full border-2 border-bronze/10"></div>
              <div className="absolute -top-2 -right-2 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-bronze/20"></div>
              <div className="absolute top-6 -right-2 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-softGold/40"></div>
            </div>
          </div>
        </div>

        {/* Bottom: Three insight cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {insights.map((item, index) => (
            <div
              key={index}
              className={`group relative bg-white rounded-xl sm:rounded-2xl p-3.5 sm:p-6 border border-warmGray/10 hover:shadow-elevated hover:-translate-y-1 transition-all duration-500 cursor-default ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{
                transitionDelay: isVisible ? `${600 + index * 150}ms` : '0ms',
                transition: 'all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              }}
            >
              <div className="flex items-start gap-3 sm:gap-4 mb-1.5 sm:mb-3">
                <span className="text-bronze/30 font-display text-xl sm:text-2xl font-light italic leading-none">
                  {item.number}
                </span>
                <div className="w-6 sm:w-8 h-[1px] bg-bronze/30 mt-2.5 sm:mt-3"></div>
              </div>
              <h3 className="font-display text-sm sm:text-base text-charcoal font-medium italic mb-1 sm:mb-2">
                {item.title}
              </h3>
              <p className="text-textGray text-[11px] sm:text-[12px] font-sans font-light leading-[1.7] sm:leading-[1.8]">
                {item.desc}
              </p>
              <div className="absolute bottom-0 left-4 sm:left-6 right-4 sm:right-6 h-[2px] bg-gradient-to-r from-transparent via-bronze/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NervousSystemShift;
